import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip/Chip';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { booleanCell } from 'components/cells';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { SuperAdminRoles, TenantRoles } from 'shared/permissions/roles';
import { IUser } from 'features/settings/users/types';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import { singleSelectFilterOperators } from 'components/enhanced';

export function useUserColumns(
  warehouse_codes: string[],
  supplier_codes: string[],
  disableUser?: (user: IUser, miniava_active?: boolean) => void,
  miniava_active?: boolean
): GridColDef[] {
  const { t } = useTranslation('');
  const groups = useAppSelector((state) => state.auth.groups);
  const isSuperAdmin = IsSuperAdminGuard();

  const columns: GridColDef[] = [];
  ['username', 'first_name', 'last_name'].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`entities.user.${field}`),
      editable: true
    });
  });

  columns.push({
    field: 'email',
    width: 250,
    editable: true,
    headerName: t(`entities.user.email`)
  });

  columns.push({
    field: 'groups',
    width: 200,
    editable: true,
    headerName: 'Ruolo',
    type: 'singleSelect',
    filterOperators: singleSelectFilterOperators,
    valueOptions: TenantRoles
  });

  if (isSuperAdmin) {
    columns.push({
      field: 'disabled',
      width: 150,
      headerName: 'Disattivato',
      type: 'boolean',
      editable: true,
      valueGetter: (params) => !params.row?.is_active,
      renderCell: booleanCell(t)
    });
  }

  columns.push({
    field: 'warehouse',
    width: 200,
    valueGetter: (params) => params.row?.warehouse ?? null,
    type: 'singleSelect',
    valueOptions: [
      { value: null, label: 'Nessuno' }, // opzione "nessuna selezione"
      ...warehouse_codes.map((code) => ({ value: code, label: code }))
    ],
    valueFormatter: (params) => params.value || '',
    headerName: t(`global.warehouse`),
    filterOperators: singleSelectFilterOperators,
    editable: true
  });

  columns.push({
    field: 'supplier',
    width: 250,
    valueGetter: (params) => params.row?.supplier,
    type: 'singleSelect',
    valueOptions: supplier_codes,
    filterOperators: singleSelectFilterOperators,
    headerName: t(`global.supplier`),
    editable: true
  });

  {
    SuperAdminRoles.some((role) => groups.includes(role)) &&
      miniava_active &&
      columns.push({
        field: 'has_ava',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (params) => params.row?.has_ava,
        valueSetter: (params) => {
          const newValue = params.value;

          return {
            ...params.row,
            has_ava: newValue
          };
        },
        type: 'boolean',
        headerName: t(`global.ava-integration`),
        editable: true,
        renderCell: ({ value }) => (
          <Chip
            label={
              value ? t('global.active-options.ACTIVE') : t('global.active-options.NOT ACTIVE')
            }
            size="small"
          />
        )
      });
  }

  columns.push({
    field: 'actions',
    width: 80,
    type: 'actions',
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      return [
        <Tooltip title={t('actions.delete')} key="delete">
          <IconButton
            onClick={() => disableUser(params.row as IUser, miniava_active)}
            color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ];
    }
  });

  return columns;
}
